import type { ContactMergedTimelineEntry } from 'views/SuperAdmin/Management/LeadManagement/util/contactMergedTimeline'
import { getContactMergedTimelineDisplay } from 'views/SuperAdmin/Management/LeadManagement/util/contactMergedTimeline'

function formatStatusLabel(status: string): string {
  const normalized = status.replace(/_/g, ' ').trim()
  if (!normalized) return ''
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function getTimestampMs(timestamp: unknown): number {
  const value = timestamp as { toMillis?: () => number; toDate?: () => Date } | Date | null | undefined
  if (value instanceof Date) return value.getTime()
  return value?.toMillis?.() ?? value?.toDate?.()?.getTime?.() ?? 0
}

/** Minimal shape of logistics rows passed from TimelineDialog. */
export type LogisticsTimelineRowForAll = {
  type: string
  key: string
  timestamp: unknown
  note?: string
  status?: string
  paid?: boolean
  mediaType?: string
  photoCount?: number
  videoCount?: number
  statusLabel?: string
  relatedUserName?: string
  whoName?: string
  categoryLabel?: string
  amount?: number
  description?: string
  legIndex?: number
  noteCategory?: string
  notes?: string
}

export type TitleTimelineRowForAll = {
  key: string
  label: string
  timestamp: Date
}

export type VehicleTimelineAllRow =
  | {
      source: 'logistics'
      sortMs: number
      key: string
      title: string
      detail?: string
    }
  | {
      source: 'title'
      sortMs: number
      key: string
      title: string
    }
  | {
      source: 'customer'
      sortMs: number
      key: string
      entry: ContactMergedTimelineEntry
      title: string
      detail: string | null
    }

export function summarizeLogisticsTimelineRow(entry: LogisticsTimelineRowForAll): {
  title: string
  detail?: string
} {
  switch (entry.type) {
    case 'vehicle_note': {
      const heading = entry.noteCategory === 'customer' ? 'Customer note' : 'Note'
      return { title: heading, detail: entry.note?.trim() || undefined }
    }
    case 'vehicle_paid_for':
      return { title: 'Paid' }
    case 'vehicle_purchased_for_inventory':
      return {
        title: 'Purchased for inventory',
        detail: entry.whoName ? `Who: ${entry.whoName}` : undefined,
      }
    case 'vehicle_funded_date':
      return { title: 'Funded' }
    case 'inventory_status_date':
      return {
        title: `Inventory: ${entry.statusLabel ?? 'Status'}`,
        detail: entry.relatedUserName ? `Customer: ${entry.relatedUserName}` : undefined,
      }
    case 'vehicle_expense':
      return {
        title: entry.categoryLabel ? `Expense: ${entry.categoryLabel}` : 'Expense',
        detail: [
          typeof entry.amount === 'number' ? `$${entry.amount.toLocaleString('en-US')}` : null,
          entry.description?.trim() || null,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
      }
    case 'manual_transporter_delivered':
      return { title: 'Marked delivered to customer' }
    case 'leg_status':
      return {
        title: `Shipment leg ${(entry.legIndex ?? 0) + 1}: ${formatStatusLabel(entry.status ?? '')}`,
        detail: entry.notes?.trim() || undefined,
      }
    case 'repair_media_group': {
      const mediaLabel =
        (entry.photoCount ?? 0) > 0 && (entry.videoCount ?? 0) > 0
          ? 'Repair media'
          : (entry.photoCount ?? 0) > 0
            ? 'Repair photos'
            : 'Repair videos'
      return { title: mediaLabel }
    }
    case 'truck_status':
      return {
        title: formatStatusLabel(entry.status ?? ''),
        detail: `Logistics status updated to ${formatStatusLabel(entry.status ?? '')}`,
      }
    case 'repair_status':
      return {
        title: `Repair: ${formatStatusLabel(entry.status ?? '')}`,
        detail: `Repair status updated to ${formatStatusLabel(entry.status ?? '')}`,
      }
    case 'repair_paid':
      return {
        title: entry.paid ? 'Repair paid' : 'Repair payment pending',
        detail: entry.paid ? 'Repair payment completed' : 'Repair payment marked pending',
      }
    case 'pdi_completed':
      return { title: 'PDI completed', detail: 'Pre-delivery inspection checklist completed' }
    case 'repair_note':
      if (entry.mediaType === 'photo') return { title: 'Repair photo', detail: entry.note }
      if (entry.mediaType === 'damage_photo') return { title: 'Damage photo', detail: entry.note }
      if (entry.mediaType === 'video') return { title: 'Repair video', detail: entry.note }
      if (entry.mediaType === 'invoice') return { title: 'Repair invoice', detail: entry.note }
      if (entry.mediaType === 'estimate') return { title: 'Repair estimate', detail: entry.note }
      return { title: 'Repair note', detail: entry.note }
    default:
      return { title: 'Logistics event' }
  }
}

export function buildVehicleTimelineAllRows(params: {
  logisticsEntries: LogisticsTimelineRowForAll[]
  titleSteps: TitleTimelineRowForAll[]
  customerEntries: ContactMergedTimelineEntry[]
}): VehicleTimelineAllRow[] {
  const rows: VehicleTimelineAllRow[] = []

  params.logisticsEntries.forEach((entry) => {
    const sortMs = getTimestampMs(entry.timestamp)
    if (!sortMs) return
    const { title, detail } = summarizeLogisticsTimelineRow(entry)
    rows.push({
      source: 'logistics',
      sortMs,
      key: `logistics-${entry.key}`,
      title,
      detail,
    })
  })

  params.titleSteps.forEach((step) => {
    rows.push({
      source: 'title',
      sortMs: step.timestamp.getTime(),
      key: `title-${step.key}`,
      title: step.label,
    })
  })

  params.customerEntries.forEach((entry) => {
    const sortMs = getTimestampMs(entry.createdAt)
    if (!sortMs) return
    const display = getContactMergedTimelineDisplay(entry)
    rows.push({
      source: 'customer',
      sortMs,
      key: `customer-${entry.id}`,
      entry,
      title: display.title,
      detail: display.body,
    })
  })

  rows.sort((a, b) => a.sortMs - b.sortMs)
  return rows
}
